import React from 'react'
import { Vertical, Horizontal, Texto, GraviGrid } from '@gravitate-js/excalibrr'
import type { Match, MatchInvoice, MatchBol } from '@api/useSupplierReconciliation/types/match'
import { Tag, Tooltip } from 'antd'
import { InvoiceActionsRenderer } from './renderers/MatchActionsRenderer'
import type {
  ColDef,
  ICellRendererParams,
  CellStyleFunc,
  ValueGetterParams,
  ValueFormatterParams,
} from 'ag-grid-community'
import { formatCurrency, currencyFormatter } from '../../shared/formatters'

interface DetailGridProps {
  data: Match // This is the parent Match object
  context: { metadata?: unknown } // Context from the parent grid
}

const SimpleBolDisplay = ({ bols }: { bols: MatchBol[] }) => {
  if (!bols || bols.length === 0) {
    return <Texto appearance='hint'>No BOLs</Texto>
  }
  return (
    <Vertical gap={4}>
      {bols.map((bol) => (
        <Tag key={bol.id} color='blue'>
          {bol.number} (Order: {bol.order_number || 'N/A'})
        </Tag>
      ))}
    </Vertical>
  )
}

// Get row height based on content (similar to FreightLineItems approach)
const getRowHeight = (invoice: MatchInvoice | undefined) => {
  if (!invoice) return 0

  // Calculate based on specific invoice properties that affect row height
  let baseHeight = 48 // Default height (increased for status tag)

  // Add extra height for voided invoices (similar to override in FreightLineItems)
  if (invoice.voided) {
    baseHeight += 10
  }

  return baseHeight
}

// Calculate total height for grid - using the same approach as FreightLineItems
const getGridHeight = (invoices: MatchInvoice[]) => {
  if (!invoices || invoices.length === 0) return 0

  // Sum of all row heights + header height + footer padding
  const rowsHeight = invoices.reduce((total, invoice) => total + getRowHeight(invoice), 0)

  // Header (40) + padding (16) + extra height for grid UI elements
  const gridUIHeight = 40 + 16

  return rowsHeight + gridUIHeight
}

// Note: formatCurrency and currencyFormatter are imported from shared/formatters

// Invoice status cell renderer
const InvoiceStatusRenderer = (params: ICellRendererParams<MatchInvoice>) => {
  const data = params.data
  if (!data) return null

  if (data.voided) {
    return (
      <Tooltip title='This invoice has been voided and is not included in the match calculations'>
        <Tag color='red'>Void</Tag>
      </Tooltip>
    )
  }

  const match = params.context?.parentMatch
  const hasBols = match?.bols?.length > 0

  if (!hasBols) {
    return (
      <Tooltip title='No associated BOL for this invoice'>
        <Tag color='orange'>Unmatched</Tag>
      </Tooltip>
    )
  }

  // Check if the match has variances that might affect this invoice
  if (match?.has_variances) {
    return (
      <Tooltip title='Has a valid BOL match, but with price/quantity variances'>
        <Tag color='gold'>Valid with Variance</Tag>
      </Tooltip>
    )
  }

  return (
    <Tooltip title='Has a valid BOL match with no variances'>
      <Tag color='green'>Valid</Tag>
    </Tooltip>
  )
}

// Define column definitions for the invoice detail grid
const getInvoiceColumnDefs = (): ColDef<MatchInvoice>[] => [
  // Status column
  {
    headerName: 'STATUS',
    flex: 1,
    minWidth: 100,
    maxWidth: 120,
    cellRenderer: InvoiceStatusRenderer,
  },

  // Invoice Number column
  {
    headerName: 'INVOICE #',
    field: 'number',
    flex: 1.5,
    cellStyle: ((params: ICellRendererParams<MatchInvoice>) => {
      return params.data?.voided ? { textDecoration: 'line-through', color: 'var(--gray-400)' } : {}
    }) as CellStyleFunc<MatchInvoice>,
  },

  // BOL Number column (if available)
  {
    headerName: 'BOL #',
    flex: 1.5,
    valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
      const bolNumbers =
        params.data?.bols?.map((bol) => bol.number).join(', ') ||
        params.context?.parentMatch?.bols?.map((bol) => bol.number).join(', ') ||
        'N/A'
      return bolNumbers
    },
    cellStyle: ((params: ICellRendererParams<MatchInvoice>) => {
      return params.data?.voided ? { textDecoration: 'line-through', color: 'var(--gray-400)' } : {}
    }) as CellStyleFunc<MatchInvoice>,
  },

  // Order Number column (if available)
  {
    headerName: 'ORDER #',
    flex: 1.5,
    valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
      // Get order numbers from either invoice BOLs or parent match BOLs
      const bols = params.data?.bols || params.context?.parentMatch?.bols || []
      const orderNumbers = new Set()

      bols.forEach((bol) => {
        if (bol.order_number) orderNumbers.add(bol.order_number)
      })

      return Array.from(orderNumbers).join(', ') || 'N/A'
    },
    cellStyle: ((params: ICellRendererParams<MatchInvoice>) => {
      return params.data?.voided ? { textDecoration: 'line-through', color: 'var(--gray-400)' } : {}
    }) as CellStyleFunc<MatchInvoice>,
  },

  // Invoice Date column - renamed to Due Date
  {
    headerName: 'DUE DATE',
    field: 'date',
    flex: 1,
    valueFormatter: (params) => {
      if (!params.value) return 'N/A'
      return new Date(params.value).toLocaleDateString()
    },
    cellStyle: ((params: ICellRendererParams<MatchInvoice>) => {
      return params.data?.voided ? { textDecoration: 'line-through', color: 'var(--gray-400)' } : {}
    }) as CellStyleFunc<MatchInvoice>,
  },

  // Source column
  {
    headerName: 'SOURCE',
    field: 'source',
    flex: 1,
    cellStyle: ((params: ICellRendererParams<MatchInvoice>) => {
      return params.data?.voided ? { textDecoration: 'line-through', color: 'var(--gray-400)' } : {}
    }) as CellStyleFunc<MatchInvoice>,
  },

  // Total column
  {
    headerName: 'TOTAL',
    field: 'total_amount',
    flex: 1,
    type: 'numericColumn',
    valueFormatter: currencyFormatter,
    cellStyle: ((params: ICellRendererParams<MatchInvoice>) => {
      return params.data?.voided ? { textDecoration: 'line-through', color: 'var(--gray-400)' } : {}
    }) as CellStyleFunc<MatchInvoice>,
  },

  // Products column (if available)
  {
    headerName: 'PRODUCTS',
    flex: 1.5,
    valueGetter: (params: ValueGetterParams<MatchInvoice>) => {
      return params.data?.products?.join(', ') || params.context?.parentMatch?.products?.join(', ') || ''
    },
    cellStyle: ((params: ICellRendererParams<MatchInvoice>) => {
      return params.data?.voided ? { textDecoration: 'line-through', color: 'var(--gray-400)' } : {}
    }) as CellStyleFunc<MatchInvoice>,
  },

  // Actions column
  {
    headerName: 'ACTIONS',
    flex: 1,
    maxWidth: 120,
    cellRenderer: (params: ICellRendererParams<MatchInvoice>) => (
      <InvoiceActionsRenderer invoice={params.data} match={params.context.parentMatch} context={params.context} />
    ),
    cellStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
]

export function MatchDetailRenderer(props: DetailGridProps) {
  const { data, context } = props // data is the parent Match object

  const hasInvoices = data.invoices && Array.isArray(data.invoices) && data.invoices.length > 0

  if (!hasInvoices) {
    return (
      <Vertical style={{ backgroundColor: 'var(--bg-2)' }} className='p-4'>
        <Texto appearance='hint'>No invoices for this match.</Texto>
      </Vertical>
    )
  }

  // Calculate the total height needed for the detail grid
  const detailHeight = getGridHeight(data.invoices)

  return (
    <Vertical style={{ height: detailHeight }}>
      <GraviGrid<MatchInvoice, any>
        primaryKey='id'
        rowData={data.invoices}
        sideBar={undefined}
        storageKey='match-invoice-detail-grid'
        columnDefs={getInvoiceColumnDefs()}
        agPropOverrides={{
          context: {
            ...context,
            parentMatch: data,
          },
          headerHeight: 32,
          scrollbarWidth: 0,
          pagination: false,
          suppressPaginationPanel: true,
          rowGroupPanelShow: 'onlyWhenGrouping',
          getRowHeight: ({ data }) => getRowHeight(data),
          getRowId: (params) => params.data.id,
          animateRows: true,
          getRowStyle: (params) => {
            if (params.data.voided) {
              return { backgroundColor: 'var(--theme-error-dim)' }
            }
            return {}
          },
          defaultColDef: {
            sortable: true,
            resizable: true,
            suppressMovable: false,
          },
        }}
      />
    </Vertical>
  )
}
